import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Card,
  CardMedia,
  Alert,
  useTheme,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  Star as StarIcon,
  Flag as FlagIcon,
  Science,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { useOffer } from '../hooks/useOffers';
import { LoadingSpinner } from '@shared/components';
import { parsePremiumIds, formatCurrency, formatDate, getOfferId, resolveImageUrl } from '@modules/offers/utils';
import { getLabelFromOptionId, getLabelsFromOptionIds } from '../utils/labelHelpers';
import { useOfferConfig } from '../contexts';
import StatusChip from '@shared/components/StatusChip/StatusChip';
import { SyncOnChip } from '../components/SyncOnChip';
import { OFFER_FIELD_TOOLTIPS } from '../constants/fieldTooltips';
import { usePlaybookFields } from '@modules/playbook/hooks/usePlaybook';
import DynamicFieldsDisplay from '../components/DynamicFieldsDisplay/DynamicFieldsDisplay';

const ViewOffer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: offer, isLoading, error } = useOffer(Number.parseInt(id));
  const { scopeOptions, currencyOptions, categoryOptions, countryOptions, premiumOptions, priceOptions } =
    useOfferConfig();
  const { data: dynamicFieldList, isLoading: dynamicFieldsLoading } = usePlaybookFields('Offer');

  const handleEdit = () => {
    navigate(`/offers/${id}/edit`);
  };

  const handleBack = () => {
    navigate('/offers');
  };

  if (isLoading) {
    return <LoadingSpinner fullHeight message="Loading offer details..." />;
  }

  if (error || !offer) {
    return (
      <Box sx={{ mx: 'auto', p: 3 }}>
        <Alert severity="error">Failed to load offer details. Please try again.</Alert>
      </Box>
    );
  }

  const premiumEntries = parsePremiumIds(offer.premium_ids?.split(',') ?? []);

  // Card Section Component (matching AddEditOffer)
  const CardSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Paper sx={{ p: 3, mb: 3, width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.text.primary }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {children}
    </Paper>
  );

  const InfoRow = ({ label, value, tooltip }: { label: string; value: React.ReactNode; tooltip?: string }) => (
    <Box sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: theme.palette.text.disabled, cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
      <Typography component="div" variant="body2" sx={{ fontWeight: 500, fontSize: 15, textAlign: 'right', flex: 1 }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ mx: 'auto', p: 1 }}>
      {/* Simple Header - Consistent with AddEditOffer */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            {offer.offer_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getOfferId(offer)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button startIcon={<BackIcon />} onClick={handleBack} variant="outlined">
            Back
          </Button>
          <Button startIcon={<EditIcon />} onClick={handleEdit} variant="contained">
            Edit Offer
          </Button>
        </Box>
      </Box>

      {/* Main Content - Sections matching AddEditOffer */}
      <Grid container spacing={3}>
        {/* Left Column - Basic Info & Pricing */}
        <Grid size={{ xs: 12, lg: 4 }} sx={{ display: 'flex' }}>
          <CardSection title="Offer Details">
            <Stack spacing={2}>
              <InfoRow label="Offer Name" value={offer.offer_name} tooltip={OFFER_FIELD_TOOLTIPS.offer_name} />
              <InfoRow
                label="Platform (Scope)"
                value={
                  <StatusChip
                    label={getLabelFromOptionId(offer.scope, scopeOptions)}
                    size="small"
                    status="primary"
                    variant="outlined"
                  />
                }
                tooltip={OFFER_FIELD_TOOLTIPS.scope}
              />
              <InfoRow
                label="Category"
                value={
                  <StatusChip
                    label={getLabelFromOptionId(offer.offer_category, categoryOptions)}
                    size="small"
                    status="info"
                    variant="outlined"
                  />
                }
                tooltip={OFFER_FIELD_TOOLTIPS.offer_category}
              />
              <InfoRow
                label="Quantity"
                value={offer.quantity?.toLocaleString() || 0}
                tooltip={OFFER_FIELD_TOOLTIPS.quantity}
              />
              <InfoRow label="Duration" value={`${offer.duration} seconds`} tooltip={OFFER_FIELD_TOOLTIPS.duration} />
              <InfoRow
                label="Price"
                value={formatCurrency(offer.price, offer.currency, currencyOptions, priceOptions)}
                tooltip={OFFER_FIELD_TOOLTIPS.price}
              />
              <InfoRow
                label="Status"
                value={<SyncOnChip value={offer.sync_on} showIcon size="small" />}
                tooltip={OFFER_FIELD_TOOLTIPS.status}
              />
            </Stack>
          </CardSection>
        </Grid>

        {/* Right Column - Availability & Targeting */}
        <Grid size={{ xs: 12, lg: 4 }} sx={{ display: 'flex' }}>
          <CardSection title="Availability & Targeting">
            <Stack spacing={2}>
              <InfoRow
                label="Start Date"
                value={offer.start_date ? formatDate(offer.start_date) : 'Not set'}
                tooltip={OFFER_FIELD_TOOLTIPS.start_date}
              />
              <InfoRow
                label="End Date"
                value={offer.end_date ? formatDate(offer.end_date) : 'Not set'}
                tooltip={OFFER_FIELD_TOOLTIPS.end_date}
              />
              <InfoRow
                label="Countries"
                value={
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      // maxWidth: '300px',
                      justifyContent: 'end',
                    }}
                  >
                    {offer.country && offer.country !== '' ? (
                      getLabelsFromOptionIds(offer.country, countryOptions).map((label, idx) => (
                        <StatusChip key={idx} label={label} size="small" status="default" variant="outlined" />
                      ))
                    ) : (
                      <StatusChip label="All Countries" size="small" status="default" />
                    )}
                  </Box>
                }
                tooltip={OFFER_FIELD_TOOLTIPS.country}
              />
              <InfoRow
                label="Age Range"
                value={
                  offer.min_user_age === 0 && offer.max_user_age === 0
                    ? 'All ages'
                    : `${offer.min_user_age} - ${offer.max_user_age} years`
                }
                tooltip="Target age range"
              />
              <InfoRow
                label="LTV"
                value={`min: ${offer.min_ltv} max: ${offer.max_ltv}`}
                tooltip={OFFER_FIELD_TOOLTIPS.ltv}
              />
              <InfoRow label="User ID" value={offer.user_id || '-'} tooltip={OFFER_FIELD_TOOLTIPS.user_id} />

              <InfoRow
                label="Flags"
                value={
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                    {offer.is_featured && (
                      <StatusChip
                        customIcon={<StarIcon />}
                        label="Featured"
                        size="small"
                        variant="outlined"
                        status="success"
                      />
                    )}
                    {offer.starter_offer && (
                      <StatusChip
                        customIcon={<FlagIcon />}
                        label="Starter Offer"
                        size="small"
                        variant="outlined"
                        status="info"
                      />
                    )}
                    {offer.is_test_offer && (
                      <StatusChip
                        customIcon={<Science />}
                        label="Test Offer"
                        size="small"
                        variant="outlined"
                        status="primary"
                      />
                    )}

                    {offer.is_shop_default && (
                      <StatusChip label="Shop Default" size="small" variant="outlined" status="default" />
                    )}
                  </Box>
                }
                tooltip="Associated game identifier"
              />
            </Stack>
          </CardSection>
        </Grid>

        {/* Premium Rewards */}
        <Grid size={{ xs: 12, lg: 4 }} sx={{ display: 'flex', justifyContent: 'space-around' }}>
          <CardSection title="Premium Rewards">
            <Stack spacing={1}>
              {premiumEntries.map((entry, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: theme.palette.primary.main + '08',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <StatusChip
                    label={`Premium ID: ${getLabelsFromOptionIds(entry.premiumId, premiumOptions)}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    Amount: {entry.amount.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardSection>
        </Grid>

        {/* Images Section */}
        <Grid size={{ xs: 12, lg: 4 }} sx={{ display: 'flex' }}>
          <CardSection title="Offer Images">
            {!offer.desktop_background &&
            !offer.mobile_background &&
            !offer.mobile_icon &&
            !offer.popup_background &&
            !offer.popup_icon ? (
              <Typography variant="body2" color="text.secondary">
                No images uploaded for this offer
              </Typography>
            ) : (
              <Stack spacing={2}>
                {offer.desktop_background && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}
                    >
                      Desktop Background
                    </Typography>
                    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                      <CardMedia
                        component="img"
                        image={resolveImageUrl(offer.desktop_background)}
                        alt="Desktop Background"
                        sx={{ height: 120, objectFit: 'contain', cursor: 'pointer' }}
                        onClick={() => window.open(resolveImageUrl(offer.desktop_background), '_blank')}
                      />
                    </Card>
                  </Box>
                )}
                {offer.mobile_background && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}
                    >
                      Mobile Background
                    </Typography>
                    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                      <CardMedia
                        component="img"
                        image={resolveImageUrl(offer.mobile_background)}
                        alt="Mobile Background"
                        sx={{ height: 120, objectFit: 'contain', cursor: 'pointer' }}
                        onClick={() => window.open(resolveImageUrl(offer.mobile_background), '_blank')}
                      />
                    </Card>
                  </Box>
                )}
                {offer.mobile_icon && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}
                    >
                      Mobile Icon
                    </Typography>
                    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                      <CardMedia
                        component="img"
                        image={resolveImageUrl(offer.mobile_icon)}
                        alt="Mobile Icon"
                        sx={{ height: 120, objectFit: 'contain', cursor: 'pointer' }}
                        onClick={() => window.open(resolveImageUrl(offer.mobile_icon), '_blank')}
                      />
                    </Card>
                  </Box>
                )}
                {offer.popup_background && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}
                    >
                      Popup Background
                    </Typography>
                    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                      <CardMedia
                        component="img"
                        image={resolveImageUrl(offer.popup_background)}
                        alt="Popup Background"
                        sx={{ height: 120, objectFit: 'contain', cursor: 'pointer' }}
                        onClick={() => window.open(resolveImageUrl(offer.popup_background), '_blank')}
                      />
                    </Card>
                  </Box>
                )}
                {offer.popup_icon && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}
                    >
                      Popup Icon
                    </Typography>
                    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                      <CardMedia
                        component="img"
                        image={resolveImageUrl(offer.popup_icon)}
                        alt="Popup Icon"
                        sx={{ height: 120, objectFit: 'contain', cursor: 'pointer' }}
                        onClick={() => window.open(resolveImageUrl(offer.popup_icon), '_blank')}
                      />
                    </Card>
                  </Box>
                )}
              </Stack>
            )}
          </CardSection>
        </Grid>

        {/* Schedule Configuration  */}
        {offer.offer_schedule && offer.offer_schedule.length > 0 && (
          <Grid size={{ xs: 12, lg: 4 }} sx={{ display: 'flex' }}>
            <CardSection title="Schedule Configuration">
              <Stack spacing={2}>
                {offer.offer_schedule.map((schedule, idx) => (
                  <Paper
                    key={idx}
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.info.main + '08',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {schedule.entry_name || `Schedule ${idx + 1}`}
                      </Typography>
                      <StatusChip label={schedule.schedule_type} size="small" status="info" />
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Start Date & Time
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                          {schedule.start_date ? formatDate(schedule.start_date, true) : '-'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          End Date & Time
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                          {schedule.end_date ? formatDate(schedule.end_date, true) : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            </CardSection>
          </Grid>
        )}

        {/* Advanced Configuration  */}
        {(offer.custom_name || offer.trigger_login_reward || offer.comment) && (
          <Grid size={{ xs: 12, lg: 4 }} sx={{ display: 'flex' }}>
            <CardSection title="Advanced Configuration">
              <Stack spacing={2}>
                {offer.custom_name && Object.keys(offer.custom_name).length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Custom Name
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem', overflow: 'auto' }}>
                        {JSON.stringify(offer.custom_name, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                {offer.trigger_login_reward && Object.keys(offer.trigger_login_reward).length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      A/B Test ID
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem', overflow: 'auto' }}>
                        {JSON.stringify(offer.trigger_login_reward, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                {offer.comment && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Comments
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {offer.comment}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Stack>
            </CardSection>
          </Grid>
        )}

        {/* Inventory Filter  */}
        <Grid size={{ xs: 12, lg: 4 }} sx={{ display: 'flex' }}>
          <CardSection title="Inventory Filter">
            <Stack spacing={2}>
              <InfoRow
                label="Hero Item ID"
                value={offer.hero_item_id || '-'}
                tooltip={OFFER_FIELD_TOOLTIPS.hero_item_id}
              />
              <InfoRow
                label="Level Item ID"
                value={offer.level_item_id || '-'}
                tooltip={OFFER_FIELD_TOOLTIPS.level_item_id}
              />
              <InfoRow
                label="Medal Item ID"
                value={offer.medal_item_id || '-'}
                tooltip={OFFER_FIELD_TOOLTIPS.medal_item_id}
              />
              <InfoRow
                label="Required Medals"
                value={offer.required_medals || '-'}
                tooltip={OFFER_FIELD_TOOLTIPS.required_medals}
              />
              <InfoRow label="Internal Comment" value={offer.comment || '-'} tooltip={OFFER_FIELD_TOOLTIPS.comment} />
            </Stack>
          </CardSection>
        </Grid>

        {/* Dynamic Fields (Custom Fields) */}
        {offer.custom_fields && offer.custom_fields.length > 0 && (
          <Grid size={{ xs: 12, lg: 4 }} sx={{ display: 'flex' }}>
            <CardSection title="Dynamic Fields">
              <Stack spacing={2}>
                <DynamicFieldsDisplay
                  customFields={offer.custom_fields}
                  dynamicFieldList={dynamicFieldList || []}
                  isLoading={dynamicFieldsLoading}
                  InfoRow={InfoRow}
                />
              </Stack>
            </CardSection>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ViewOffer;
